import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import stringUtils from '../../../../qti/utils/StringUtils';
import { t } from '../../../../services/i18nService';
import styles from './inlineChoiceInteraction.scss';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import constants from '../../../../qti/Constants';
import inlineChoiceService from '../../../../services/questionView/InlineChoiceService';
import a11yIIBScreenReaderService from '../../../../services/a11yIIBScreenReaderService';

const DefaultSelectInlineChoice = ({
    alternatives,
    value,
    handleAlternativeBoxClick,
    focusElement,
    questionProps,
}) => {
    const [classes, setClasses] = useState([styles.inlineSelectChoiceInteraction]);

    const instanceId = stringUtils.getUID();
    const {
        currentScorableItem,
        isReadOnly,
        model,
        questionModel,
        flaggedItems,
        callback,
    } = questionProps;
    const interactionNumber = scorableItemNumberingService.getInteractionNumber(questionModel.contentRevisionId, model.identifier);
    const isItemPreSelected = currentScorableItem === Number(interactionNumber);
    const isFlagged = flaggedItems && flaggedItems.includes(`${questionModel.contentRevisionId}-${interactionNumber}`);
    const isA11yIterationForInlineChoiceEnabled = inlineChoiceService.isA11yIterationForInlineChoiceEnabled();

    useEffect(() => {
        if (isFlagged && model.hasGranularNumbering) {
            setClasses(prev => [...prev, 'flagged']);
        } else {
            setClasses([styles.inlineSelectChoiceInteraction]);
        }
    }, [isFlagged, model.hasGranularNumbering]);

    const handleSelectMenuUserResponse = (selectEvent) => {
        const options = selectEvent.currentTarget.options;

        // Check for the "no choice made" option.
        if (options[options.selectedIndex]?.value === constants.noChoiceMadeOption) {
            callback(model.identifier, []);
            return;
        }

        // Deal with the actual options.
        const optionsValue = Array.from(options)
            .filter(option => option.selected)
            .map(option => option.value);

        callback(model.identifier, optionsValue);
    };

    const getOptions = () => {
        const alternativeOptions = alternatives.map((alternative) => {
            const { content, identifier } = alternative;
            return (<option
                key={identifier}
                value={identifier}
            >
                {content}
            </option>);
        });

        const additionalOptions = [
            // no choice made option
            <option
                key={constants.noChoiceMadeOption}
                value={constants.noChoiceMadeOption}
                label={t('interactions.selectAlternative')}
            />,
        ];

        if (model.hasGranularNumbering) {
            additionalOptions.unshift(
                <option
                    key="interaction_number"
                    value={constants.noChoiceMadeOption}
                    label={interactionNumber}
                    hidden
                />
            );
        }

        alternativeOptions.unshift(...additionalOptions);

        return alternativeOptions;
    };

    const getSelectComponent = () => {
        const selectProps = {};
        if (isA11yIterationForInlineChoiceEnabled) {
            const selectAriaCaption = inlineChoiceService.getInlineChoiceSelectAriaLabelCaption(questionProps.model,
                questionProps.questionModel.responseModels);
            if (selectAriaCaption) {
                selectProps['aria-label'] = selectAriaCaption;
            }
        }
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            selectProps['aria-label'] = a11yIIBScreenReaderService.getAriaLabelValue(
                selectProps['aria-label'],
                t('interactions.inlineChoice.fieldDropDown')
            );
        }
        return (
            <select
                {...selectProps}
                value={value}
                id={instanceId}
                disabled={isReadOnly}
                name={instanceId}
                onChange={handleSelectMenuUserResponse}
                onClick={handleAlternativeBoxClick}
                className={classes.join(' ')}
                ref={element => model.hasGranularNumbering && focusElement(isItemPreSelected, element)}
                style={{ textAlign: (!value || value === constants.noChoiceMadeOption) && model.hasGranularNumbering ? 'center' : 'initial' }}
            >
                {getOptions()}
            </select>
        );
    };

    if (isA11yIterationForInlineChoiceEnabled) {
        return getSelectComponent();
    }

    return (
        <label htmlFor={instanceId} aria-label={t('interactions.inlineChoice.fieldWrapperAriaLabel')}>
            {getSelectComponent()}
        </label>
    );
};

DefaultSelectInlineChoice.propTypes = {
    alternatives: PropTypes.array.isRequired,
    value: PropTypes.string,
    handleAlternativeBoxClick: PropTypes.func.isRequired,
    focusElement: PropTypes.func.isRequired,
    questionProps: PropTypes.object.isRequired,
};

export default DefaultSelectInlineChoice;
